import React, { useCallback, useEffect } from "react";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { mediaUrl } from "../lib/api";

export default function Lightbox({ images = [], index, onClose, onIndex }) {
  const open = index !== null && index >= 0 && images.length > 0;

  const next = useCallback(() => onIndex((index + 1) % images.length), [index, images.length, onIndex]);
  const prev = useCallback(
    () => onIndex((index - 1 + images.length) % images.length),
    [index, images.length, onIndex]
  );

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose, next, prev]);

  if (!open) return null;
  const current = images[index];
  const url = mediaUrl(typeof current === "string" ? current : current?.url, 1920);

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      style={{ background: "rgba(5,7,6,0.95)" }}
      data-testid="lightbox-dialog"
      role="dialog"
      aria-modal="true"
    >
      <button
        type="button"
        onClick={onClose}
        aria-label="Close"
        className="absolute right-4 top-4 rounded-lg border p-2 text-[color:var(--nq-sand)] hover:border-[color:var(--nq-gold)]"
        style={{ borderColor: "rgba(255,255,255,0.15)" }}
        data-testid="lightbox-close-button"
      >
        <X className="h-5 w-5" />
      </button>

      {images.length > 1 && (
        <>
          <button
            type="button"
            onClick={prev}
            aria-label="Previous image"
            className="absolute left-3 rounded-full border p-2.5 hover:border-[color:var(--nq-gold)]"
            style={{ borderColor: "rgba(255,255,255,0.15)" }}
            data-testid="lightbox-prev-button"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <button
            type="button"
            onClick={next}
            aria-label="Next image"
            className="absolute right-3 rounded-full border p-2.5 hover:border-[color:var(--nq-gold)]"
            style={{ borderColor: "rgba(255,255,255,0.15)" }}
            data-testid="lightbox-next-button"
          >
            <ChevronRight className="h-5 w-5" />
          </button>
        </>
      )}

      <div className="flex max-h-full max-w-6xl flex-col items-center gap-3">
        <img src={url} alt="" className="max-h-[80vh] max-w-full rounded-lg object-contain" />
        <p className="text-xs text-[color:var(--nq-stone)]">
          {index + 1} / {images.length}
        </p>
      </div>
    </div>
  );
}
